import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';
import { COOKIE_CONSENT_EVENT } from '../lib/consent';
import { MOCK_PROPERTIES, toPropertyCardFormat, USE_MOCK_DATA } from '../mock/index';
import { SEOHead } from './SEOHead';
import {
  createBreadcrumbSchema,
  createVacationRentalSchema,
  lodgingBusinessSchema,
  websiteSchema,
} from './StructuredData';

const SITE_URL = 'https://sunmarbella.com';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&h=630&fit=crop';

interface RouteMetaConfig {
  title: string;
  description: string;
  canonical: string;
  type?: string;
  images?: Array<{ url: string; alt: string }>;
  jsonLd?: Record<string, unknown>[];
}

function getStaticRouteMeta(pathname: string): RouteMetaConfig {
  switch (pathname) {
    case '/':
      return {
        title: 'Marbella Family Rentals | Book Direct | SUNMARBELLA',
        description:
          'Inspected family rentals in Marbella. Better direct rates. Real local support. Book direct with SUNMARBELLA.',
        canonical: `${SITE_URL}/`,
        type: 'website',
        images: [{ url: DEFAULT_IMAGE, alt: 'Luxury holiday rental with pool in Marbella' }],
        jsonLd: [websiteSchema, lodgingBusinessSchema],
      };
    case '/destinations':
      return {
        title: 'Marbella Rentals Collection | Search by Area, Size, and Features',
        description:
          'Search inspected Marbella rentals by area, size, features, and price. Book direct with a local team.',
        canonical: `${SITE_URL}/destinations`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Selection of rentals across Marbella' }],
        jsonLd: [
          createBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Destinations', url: `${SITE_URL}/destinations` },
          ]),
        ],
      };
    case '/collections':
      return {
        title: 'Marbella Rental Collections | SUNMARBELLA',
        description:
          'Beachfront, family-ready, golfside, or full privacy. Browse curated Marbella rental collections.',
        canonical: `${SITE_URL}/collections`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Curated rental collections in Marbella' }],
        jsonLd: [
          createBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Collections', url: `${SITE_URL}/collections` },
          ]),
        ],
      };
    case '/services':
      return {
        title: 'Marbella Guest Services & Local Support | SUNMARBELLA',
        description:
          'Real Marbella support before, during, and after your stay. Fast answers. Local help. No platform runaround.',
        canonical: `${SITE_URL}/services`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Marbella guest services and local support' }],
        jsonLd: [
          createBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Services', url: `${SITE_URL}/services` },
          ]),
        ],
      };
    case '/contact':
      return {
        title: 'Contact SUNMARBELLA | Rental Booking, Owner Enquiries, and Local Support',
        description:
          'Tell SUNMARBELLA your dates, group, or property details. We reply with clear next steps.',
        canonical: `${SITE_URL}/contact`,
        images: [{ url: DEFAULT_IMAGE, alt: 'SUNMARBELLA guest support and contact' }],
        jsonLd: [
          createBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Contact', url: `${SITE_URL}/contact` },
          ]),
        ],
      };
    case '/about':
      return {
        title: 'About SUNMARBELLA | Local Family Rental Experts',
        description:
          'Guests book with confidence, and owners work with a real Marbella team that protects standards from first enquiry to check-out.',
        canonical: `${SITE_URL}/about`,
        images: [{ url: DEFAULT_IMAGE, alt: 'About SUNMARBELLA local rental experts' }],
        jsonLd: [
          createBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'About', url: `${SITE_URL}/about` },
          ]),
        ],
      };
    case '/owners':
      return {
        title: 'Property Owners in Marbella | Partner With SUNMARBELLA',
        description:
          'Selective partnership for Marbella owners who want stronger presentation, better guest fit, and local oversight.',
        canonical: `${SITE_URL}/owners`,
        images: [{ url: DEFAULT_IMAGE, alt: 'SUNMARBELLA owner partnerships in Marbella' }],
        jsonLd: [
          createBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Owners', url: `${SITE_URL}/owners` },
          ]),
        ],
      };
    case '/privacy-policy':
      return {
        title: 'Privacy & Cookie Policy | SUNMARBELLA',
        description:
          'Learn how SUNMARBELLA handles privacy, cookies, analytics, browser storage, and your rights.',
        canonical: `${SITE_URL}/privacy-policy`,
        images: [{ url: DEFAULT_IMAGE, alt: 'SUNMARBELLA privacy and cookie policy' }],
      };
    case '/cookie-policy':
      return {
        title: 'Cookie Policy | SUNMARBELLA',
        description:
          'Learn how SUNMARBELLA uses cookies, analytics, and browser storage on this website.',
        canonical: `${SITE_URL}/cookie-policy`,
        images: [{ url: DEFAULT_IMAGE, alt: 'SUNMARBELLA cookie policy' }],
      };
    case '/booking-terms':
      return {
        title: 'Booking Terms & Conditions | SUNMARBELLA',
        description:
          'Read the key booking terms for SUNMARBELLA stays, including payments, cancellations, guest obligations, complaints, and liability.',
        canonical: `${SITE_URL}/booking-terms`,
        images: [{ url: DEFAULT_IMAGE, alt: 'SUNMARBELLA booking terms and conditions' }],
      };
    case '/marbella-golden-mile':
      return {
        title: 'Family Rentals on the Golden Mile, Marbella | SUNMARBELLA',
        description:
          'Golden Mile family rentals with private pools, prestige, and direct booking through SUNMARBELLA.',
        canonical: `${SITE_URL}/marbella-golden-mile`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Family rentals on Marbella Golden Mile' }],
      };
    case '/nueva-andalucia':
      return {
        title: 'Family Rentals in Nueva Andalucia, Marbella | SUNMARBELLA',
        description:
          'Nueva Andalucia rentals with golf, pools, and quieter streets. Book direct with SUNMARBELLA.',
        canonical: `${SITE_URL}/nueva-andalucia`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Family rentals in Nueva Andalucia' }],
      };
    case '/puerto-banus':
      return {
        title: 'Family Rentals Near Puerto Banus, Marbella | SUNMARBELLA',
        description:
          'Family rentals near Puerto Banus. Marina, beach, restaurants, and direct local support.',
        canonical: `${SITE_URL}/puerto-banus`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Family rentals near Puerto Banus' }],
      };
    case '/elviria':
      return {
        title: 'Family Rentals in Elviria, Marbella East | SUNMARBELLA',
        description:
          'Elviria family rentals with quieter streets, beach access, and private pools. Book direct.',
        canonical: `${SITE_URL}/elviria`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Family rentals in Elviria Marbella East' }],
      };
    case '/sierra-blanca':
      return {
        title: 'Luxury Family Rentals in Sierra Blanca, Marbella | SUNMARBELLA',
        description:
          'Sierra Blanca rentals with privacy, prestige, and direct local support.',
        canonical: `${SITE_URL}/sierra-blanca`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Luxury family rentals in Sierra Blanca' }],
      };
    default:
      return {
        title: 'SUNMARBELLA | Luxury Holiday Rentals in Marbella',
        description:
          'Inspected Marbella rentals, better direct rates, real local support.',
        canonical: `${SITE_URL}${pathname}`,
        images: [{ url: DEFAULT_IMAGE, alt: 'Luxury holiday rentals in Marbella' }],
      };
  }
}

function getPropertyRouteMeta(pathname: string): RouteMetaConfig | null {
  if (!pathname.startsWith('/property/')) return null;

  const propertyId = pathname.split('/property/')[1]?.split('/')[0];
  if (!propertyId) return null;

  const property = MOCK_PROPERTIES.find((item) => item.lodging_id === propertyId);

  if (!property) {
    return {
      title: 'Rental Details | SUNMARBELLA',
      description:
        'Discover premium rental details, availability, and booking support with SUNMARBELLA.',
      canonical: `${SITE_URL}${pathname}`,
      images: [{ url: DEFAULT_IMAGE, alt: 'Rental details in Marbella' }],
      jsonLd: [
        createBreadcrumbSchema([
          { name: 'Home', url: `${SITE_URL}/` },
          { name: 'Destinations', url: `${SITE_URL}/destinations` },
          { name: 'Property', url: `${SITE_URL}${pathname}` },
        ]),
      ],
    };
  }

  const transformedProperty = toPropertyCardFormat(property);

  return {
    title: `${property.lodging_name} - Family Rental Stay in Marbella`,
    description: property.headline,
    canonical: `${SITE_URL}${pathname}`,
    type: 'article',
    images: [{ url: property.images[0]?.url || DEFAULT_IMAGE, alt: property.lodging_name }],
    jsonLd: [
      createVacationRentalSchema({
        ...transformedProperty,
        description: property.headline,
      }),
      createBreadcrumbSchema([
        { name: 'Home', url: `${SITE_URL}/` },
        { name: 'Destinations', url: `${SITE_URL}/destinations` },
        { name: property.lodging_name, url: `${SITE_URL}${pathname}` },
      ]),
    ],
  };
}

export const RouteMeta: React.FC = () => {
  const location = useLocation();
  const analyticsPath = `${location.pathname}${location.search}${location.hash}`;

  const meta = useMemo(() => {
    const propertyMeta = getPropertyRouteMeta(location.pathname);
    if (propertyMeta) return propertyMeta;
    return getStaticRouteMeta(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    trackPageView(analyticsPath, meta.title);
  }, [analyticsPath, meta.title]);

  useEffect(() => {
    const handleConsentChange = () => {
      trackPageView(analyticsPath, meta.title);
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
  }, [analyticsPath, meta.title]);

  return (
    <SEOHead
      title={meta.title}
      description={meta.description}
      canonical={meta.canonical}
      type={meta.type}
      images={meta.images}
      jsonLd={meta.jsonLd}
    />
  );
};
